import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, TextField, InputAdornment, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Controller } from 'react-hook-form';
import SaveIcon from '@mui/icons-material/Save';

function VitalsForm({ control, watch, setValue, errors, onSave, saving, canSave }) {
  const theme = useTheme();
  const primaryColor = theme.palette.logo?.main || '#00798E';

  const weight = watch('weight');
  const height = watch('height');

  useEffect(() => {
    if (weight && height) {
      const heightInMeters = parseFloat(height) / 100;
      if (heightInMeters > 0) {
        const bmi = (parseFloat(weight) / (heightInMeters * heightInMeters)).toFixed(2);
        setValue('bmi', bmi);
      }
    } else {
      setValue('bmi', '');
    }
  }, [weight, height, setValue]);

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'white',
      '&:hover fieldset': { borderColor: primaryColor },
      '&.Mui-focused fieldset': { borderColor: primaryColor, borderWidth: 2 },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: primaryColor },
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Row 1: Weight, Height, BMI */}
        <Grid item xs={6} sm={4} md={4}>
          <Controller
            name="weight"
            control={control}
            rules={{ required: 'Weight is required', min: { value: 1, message: 'Must be greater than 0' } }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Weight"
                required
                variant="outlined"
                size="small"
                fullWidth
                type="number"
                error={!!errors?.weight}
                helperText={errors?.weight?.message}
                InputProps={{
                  endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                }}
                sx={inputSx}
              />
            )}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={4}>
          <Controller
            name="height"
            control={control}
            rules={{ required: 'Height is required', min: { value: 1, message: 'Must be greater than 0' } }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Height"
                required
                variant="outlined"
                size="small"
                fullWidth
                type="number"
                error={!!errors?.height}
                helperText={errors?.height?.message}
                InputProps={{
                  endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                }}
                sx={inputSx}
              />
            )}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={4}>
          <Controller
            name="bmi"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="BMI (Auto)"
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{
                  ...inputSx,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f5f5f5',
                    '&:hover fieldset': { borderColor: '#ccc' },
                  },
                }}
              />
            )}
          />
        </Grid>

        {/* Row 2: BP Systolic, BP Diastolic, Pulse */}
        <Grid item xs={6} sm={4} md={4}>
          <Controller
            name="bp_systolic"
            control={control}
            rules={{ required: 'BP Systolic is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="BP Systolic"
                required
                variant="outlined"
                size="small"
                fullWidth
                type="number"
                error={!!errors?.bp_systolic}
                helperText={errors?.bp_systolic?.message}
                InputProps={{
                  endAdornment: <InputAdornment position="end">mmHg</InputAdornment>,
                }}
                sx={inputSx}
              />
            )}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={4}>
          <Controller
            name="bp_diastolic"
            control={control}
            rules={{ required: 'BP Diastolic is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="BP Diastolic"
                required
                variant="outlined"
                size="small"
                fullWidth
                type="number"
                error={!!errors?.bp_diastolic}
                helperText={errors?.bp_diastolic?.message}
                InputProps={{
                  endAdornment: <InputAdornment position="end">mmHg</InputAdornment>,
                }}
                sx={inputSx}
              />
            )}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={4}>
          <Controller
            name="pulse"
            control={control}
            rules={{ required: 'Pulse is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Pulse"
                required
                variant="outlined"
                size="small"
                fullWidth
                type="number"
                error={!!errors?.pulse}
                helperText={errors?.pulse?.message}
                InputProps={{
                  endAdornment: <InputAdornment position="end">bpm</InputAdornment>,
                }}
                sx={inputSx}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Save Button */}
      {canSave !== false && (
        <Box display="flex" justifyContent="center" mt={3}>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            style={{
              padding: '10px 28px',
              background: saving ? '#ccc' : '#00798E',
              color: saving ? '#999' : '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'background 0.3s ease',
            }}
            onMouseOver={(e) => { if (!saving) e.currentTarget.style.background = '#006379'; }}
            onMouseOut={(e) => { if (!saving) e.currentTarget.style.background = '#00798E'; }}
          >
            <SaveIcon fontSize="small" />
            {saving ? 'Saving...' : 'Save Vitals'}
          </button>
        </Box>
      )}
    </Box>
  );
}

VitalsForm.propTypes = {
  control: PropTypes.object.isRequired,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  errors: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  canSave: PropTypes.bool,
};

VitalsForm.defaultProps = {
  errors: {},
  saving: false,
  canSave: true,
};

export default VitalsForm;
